// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { Session } from "@insite/client-framework/Services/SessionService";
import translate from "@insite/client-framework/Translate";
import { QuoteModel, QuoteSettingsModel } from "@insite/client-framework/Types/ApiModels";
import RfqQuoteDetailsPageTypeLink from "@insite/content-library/Components/RfqQuoteDetailsPageTypeLink";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { EMPTY_VALUE, sharedDateTimeOptions } from "@werner/XDL/constants";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    quote: QuoteModel;
    session: Session;
    quoteSettings: QuoteSettingsModel;
    extendedStyles?: QuoteSummaryCardStyles;
}

type Props = OwnProps;

export interface QuoteSummaryCardStyles {
    quoteSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    quoteDetailsLinkGridItem?: GridItemProps;
    typeGridItem?: GridItemProps;
    typeHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    orderDateGridItem?: GridItemProps;
    orderDateHeadingAndText?: SmallHeadingAndTextStyles;
    expirationDateGridItem?: GridItemProps;
    expirationDateHeadingAndText?: SmallHeadingAndTextStyles;
    salesRepGridItem?: GridItemProps;
    salesRepHeadingAndText?: SmallHeadingAndTextStyles;
    userNameGridItem?: GridItemProps;
    userNameHeadingAndText?: SmallHeadingAndTextStyles;
    customerGridItem?: GridItemProps;
    customerHeadingAndText?: SmallHeadingAndTextStyles;
    shipToAddressGridItem?: GridItemProps;
    shipToAddressHeadingAndText?: SmallHeadingAndTextStyles;
    dataCell?: DataTableCellProps;
}

export const quoteSummaryCardStyles: QuoteSummaryCardStyles = {};

const QuoteSummaryCard: FC<Props> = ({ quote, session, quoteSettings, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(quoteSummaryCardStyles, extendedStyles));

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: quote.orderDate!,
        language: session.language,
        options: {
            ...sharedDateTimeOptions,
        },
    });

    let expirationDateDisplay = "";
    if (quote.expirationDate) {
        expirationDateDisplay = getLocalizedDateTime({
            dateTime: quote.expirationDate,
            language: session.language,
            options: {
                ...sharedDateTimeOptions,
            },
        });
    }

    // XNG-Changes: change structure to Table
    const shipToNickName = quote.shipTo?.attention || quote.shipTo?.companyName;
    const customerNumber = quote.shipTo?.customerSequence || quote.shipTo?.customerNumber;
    return (
        <>
            <DataTableCell data-th={translate("Quote #")} {...styles.dataCell}>
                <RfqQuoteDetailsPageTypeLink title={quote.erpOrderNumber || quote.quoteNumber} quoteId={quote.id} />
            </DataTableCell>
            <DataTableCell data-th={translate("Status")} {...styles.dataCell}>
                {quote.statusDisplay}
            </DataTableCell>
            <DataTableCell data-th={translate("Requested")} {...styles.dataCell}>
                {orderDateDisplay || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("PO #")} {...styles.dataCell}>
                {quote.poNumber || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("Expires")} {...styles.dataCell}>
                {expirationDateDisplay || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("User")} {...styles.dataCell}>
                {quote.userName || EMPTY_VALUE}
            </DataTableCell>
            <DataTableCell data-th={translate("Ship-To")} {...styles.dataCell}>
                {`${shipToNickName} #${customerNumber}` || EMPTY_VALUE}
            </DataTableCell>
        </>
    );
};

export default QuoteSummaryCard;
